import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getEvents } from '@redux/features/eventsSlice';
import { getCities, getCityOptions } from 'helpers/citiesUtils';
import { getDanceStyleFromPath } from 'helpers/danceStyleUtils';
import { filterEventsByParams } from 'helpers/eventFilterUtils';
import { getEventCountry } from 'helpers/eventUtils';

/**
 * Competitions imported from e-phan can happen in countries where ECDS has no
 * member organization, so the dropdowns take whatever the events themselves add
 * on top of the organization-derived lists.
 */
const withEventValues = (options, events, pick) =>
  [...new Set([...options, ...events.map(pick).filter(Boolean)])].sort();

export const useEvents = ({ organizations, searchFormState, setSearchFormState }) => {
  const { isLoading, error, events } = useSelector((state) => state.events);
  const [eventsList, setEventsList] = useState([]);

  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const { pathname, search } = useLocation();
  const dispatch = useDispatch();

  const eventTenses = ['future events', 'past events'];
  const URLParams = new URLSearchParams(search);
  const initialActiveTense = URLParams.get('type')
    ? eventTenses.findIndex((tense) => tense === URLParams.get('type'))
    : 0;
  const [activeEventTenseIndex, setActiveEventTenseIndex] = useState(initialActiveTense);

  // The city dataset behind the filter is the heaviest thing the site can
  // download, and nobody needs it until they actually use the filters. Waiting
  // for the first interaction keeps it off the critical path here and out of a
  // crawler's render altogether — Googlebot renders the page but never touches
  // it, and the calendar is one of the pages most worth indexing.
  const [isVisitorActive, setIsVisitorActive] = useState(false);

  useEffect(() => {
    const signals = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    const wake = () => setIsVisitorActive(true);

    signals.forEach((signal) => window.addEventListener(signal, wake, { once: true, passive: true }));

    return () => signals.forEach((signal) => window.removeEventListener(signal, wake));
  }, []);

  useEffect(() => {
    if (!organizations.length) return undefined;

    const filteredByCountry = Boolean(events.length && searchFormState.country);

    setCountryOptions(withEventValues(organizations.map(({ country }) => country), events, getEventCountry));

    if (filteredByCountry) {
      setSearchFormState((prevState) => {
        const { city, ...restState } = prevState;
        return restState;
      });
    }

    // Until the dataset arrives — and if it never does, because it wasn't
    // asked for or the chunk failed to load — the dropdown still offers the
    // cities the events themselves name.
    let cancelled = false;

    const setCities = (citiesByCountry) => {
      if (cancelled) return;

      setCityOptions(
        filteredByCountry
          ? withEventValues(
              citiesByCountry[searchFormState.country] ?? [],
              events.filter((event) => getEventCountry(event) === searchFormState.country),
              ({ city }) => city
            )
          : withEventValues(getCityOptions(citiesByCountry), events, ({ city }) => city)
      );
    };

    setCities({});
    if (isVisitorActive) {
      getCities(organizations).then(setCities, () => {});
    }

    return () => {
      cancelled = true;
    };
  }, [organizations, events, searchFormState.country, isVisitorActive]);

  useEffect(() => {
    const danceStyle = getDanceStyleFromPath(pathname);
    if (danceStyle && !events.length) {
      dispatch(getEvents({ danceStyle }));
    }
  }, []);

  useEffect(() => {
    if (events.length) {
      setSearchFormState({});
    }
  }, [activeEventTenseIndex]);

  useEffect(() => {
    if (events.length) {
      setEventsList(filterEventsByParams(events, null, activeEventTenseIndex));
    }
  }, [events, activeEventTenseIndex]);

  useEffect(() => {
    if (events.length && (searchFormState.name || searchFormState.country || searchFormState.city)) {
      handleSearch();
    }
  }, [events]);

  const handleSearch = () => {
    setEventsList(filterEventsByParams(events, searchFormState, activeEventTenseIndex));
  };

  const handleClear = () => {
    setEventsList(filterEventsByParams(events, null, activeEventTenseIndex));
  };

  return {
    isLoading,
    error,
    eventsList,
    setEventsList,
    eventTenses,
    activeEventTenseIndex,
    setActiveEventTenseIndex,
    countryOptions,
    cityOptions,
    handleSearch,
    handleClear,
  };
};
